import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, TextField, InputAdornment, Card, CardContent,
  Accordion, AccordionSummary, AccordionDetails, Chip, IconButton,
  CircularProgress, Alert, Tabs, Tab, Breadcrumbs, Link as MuiLink
} from '@mui/material';
import {
  Search as SearchIcon, ExpandMore as ExpandMoreIcon,
  ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import supportService from '../services/supportService';

const SupportFaqPage = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { idOrSlug } = useParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [article, setArticle] = useState(null);
  const [voted, setVoted] = useState({});
  const debounceTimer = useRef(null);

  const fetchFaq = useCallback(async (search = '') => {
    if (search) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }
    try {
      const params = {};
      if (search) params.search = search;
      const res = await supportService.getFaq(params);
      if (res.success) setCategories(res.data || []);
    } catch (err) {
      console.error('[SupportFaqPage] fetchFaq error:', err);
      setError(t('support:errors.loadError'));
    } finally {
      if (search) {
        setIsSearching(false);
      } else {
        setLoading(false);
      }
    }
  }, [t]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Débounce: clear previous timer and set new one
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchFaq(value);
    }, 600); // 600ms delay to reduce server load
  };

  const fetchArticle = useCallback(async (slug) => {
    setLoading(true);
    try {
      const res = await supportService.getFaqArticle(slug);
      if (res.success) setArticle(res.data);
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (idOrSlug) {
      fetchArticle(idOrSlug);
    } else {
      fetchFaq();
    }
    
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [idOrSlug, fetchFaq, fetchArticle]);

  const handleVote = async (articleId, vote) => {
    if (voted[articleId]) return;
    try {
      const res = await supportService.voteFaqArticle(articleId, vote);
      if (res.success) {
        setVoted(prev => ({ ...prev, [articleId]: vote }));
        // Reload article to get updated vote counts
        if (article && article.id === articleId) {
          const updatedArticle = await supportService.getFaqArticle(article.slug || article.id);
          if (updatedArticle.success) setArticle(updatedArticle.data);
        }
      }
    } catch (err) {
      console.error('[Vote error]:', err);
    }
  };

  const filteredCategories = selectedCategory === 'all'
    ? categories
    : categories.filter(c => c.id === selectedCategory);

  // Single article view
  if (article) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/support/faq" underline="hover" color="inherit">
            {t('support:faq.title')}
          </MuiLink>
          <Typography color="text.primary">{article.question}</Typography>
        </Breadcrumbs>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              {article.question}
            </Typography>
            <Box
              sx={{ mt: 2, '& p': { mb: 2 }, '& h2': { mt: 3, mb: 1 }, '& ul': { pl: 3 } }}
              dangerouslySetInnerHTML={{ __html: article.answer }}
            />

            <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('support:faq.helpfulQ')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  {article.thumbsUp || 0}
                </Typography>
                <IconButton
                  onClick={() => handleVote(article.id, 'up')}
                  color={voted[article.id] === 'up' ? 'primary' : 'default'}
                  size="small"
                >
                  <ThumbUpIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  {article.thumbsDown || 0}
                </Typography>
                <IconButton
                  onClick={() => handleVote(article.id, 'down')}
                  color={voted[article.id] === 'down' ? 'error' : 'default'}
                  size="small"
                >
                  <ThumbDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 3,
          py: 2,
          px: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
          {t('support:faq.title')}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0, color: 'white' }}>
          {t('support:faq.subtitle', 'Trouvez les réponses à vos questions')}
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder={t('support:faq.searchPlaceholder')}
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      {/* Category tabs */}
      {categories.length > 1 && (
        <Tabs
          value={selectedCategory}
          onChange={(_, val) => setSelectedCategory(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab label={t('support:faq.allCategories')} value="all" />
          {categories.map(cat => (
            <Tab key={cat.id} label={cat.name} value={cat.id} />
          ))}
        </Tabs>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredCategories.length === 0 ? (
        <Alert severity="info">{searchQuery ? t('support:faq.noResults') : t('support:faq.empty')}</Alert>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {isSearching && (
            <Box sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '0.875rem',
              color: 'text.secondary'
            }}>
              <CircularProgress size={16} />
              Recherche en cours...
            </Box>
          )}
          {filteredCategories.map(category => (
          <Box key={category.id} sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={500} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {category.icon && <span>{category.icon}</span>}
              {category.name}
              <Chip label={category.articles?.length || 0} size="small" sx={{ ml: 1 }} />
            </Typography>
            {category.articles?.map(art => (
              <Accordion key={art.id} sx={{ '&:before': { display: 'none' }, mb: 1, border: 1, borderColor: 'divider', borderRadius: '8px !important', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>{art.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box dangerouslySetInnerHTML={{ __html: art.answer?.substring(0, 500) }} />
                  {art.answer?.length > 500 && (
                    <MuiLink
                      component={Link}
                      to={`/support/faq/${art.slug || art.id}`}
                      sx={{ mt: 1, display: 'inline-block' }}
                    >
                      {t('support:faq.readMore')}
                    </MuiLink>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('support:faq.helpfulQ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight={600}>
                        {art.thumbsUp || 0}
                      </Typography>
                      <IconButton onClick={() => handleVote(art.id, 'up')} size="small" color={voted[art.id] === 'up' ? 'primary' : 'default'}>
                        <ThumbUpIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight={600}>
                        {art.thumbsDown || 0}
                      </Typography>
                      <IconButton onClick={() => handleVote(art.id, 'down')} size="small" color={voted[art.id] === 'down' ? 'error' : 'default'}>
                        <ThumbDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default SupportFaqPage;
